import { PrismaClient } from "@prisma/client";
import {
  withDbBroadcastCircuit,
  withDbExportCircuit,
  withDbReportingCircuit,
  withDbTransactionalCircuit,
} from "@sajilni/resilience";

type PrismaPool = "transactional" | "broadcast" | "reporting" | "export";

function createBasePrismaClient(datasourceUrl?: string) {
  return new PrismaClient({
    ...(datasourceUrl ? { datasourceUrl } : {}),
    log: [
      { emit: "stdout", level: "warn" },
      { emit: "stdout", level: "error" },
    ],
  });
}

/** Inferred from `log` so `$on("query", …)` is allowed (plain `PrismaClient` widens to no events). */
type PrismaClientWithQueryEvents = ReturnType<typeof createBasePrismaClient>;
type AnyFunction = (...args: unknown[]) => unknown;
type CircuitRunner = <T>(fn: () => Promise<T>) => Promise<T>;

const PRISMA_PROXY_SKIP = new Set([
  "$on",
  "$use",
  "$connect",
  "$disconnect",
  "$extends",
  "$transaction",
]);

function wrapPrismaWithCircuit<TClient extends object>(
  client: TClient,
  withCircuit: CircuitRunner
): TClient {
  const delegateProxyCache = new WeakMap<object, object>();
  const clientMethodCache = new Map<PropertyKey, unknown>();

  function wrapModelDelegate<TDelegate extends object>(
    delegate: TDelegate
  ): TDelegate {
    const cached = delegateProxyCache.get(delegate);
    if (cached) {
      return cached as TDelegate;
    }

    const methodCache = new Map<PropertyKey, unknown>();
    const wrappedDelegate = new Proxy(delegate, {
      get(target, prop, receiver) {
        const value = Reflect.get(target, prop, receiver);

        if (typeof value === "function") {
          if (!methodCache.has(prop)) {
            methodCache.set(prop, (...args: unknown[]) =>
              withCircuit(async () =>
                Reflect.apply(value as AnyFunction, target, args)
              )
            );
          }

          return methodCache.get(prop);
        }

        return value;
      },
    }) as TDelegate;

    delegateProxyCache.set(delegate, wrappedDelegate);
    return wrappedDelegate;
  }

  function wrapTransaction(value: AnyFunction, target: TClient) {
    return (...args: unknown[]) => {
      if (typeof args[0] === "function") {
        const callback = args[0] as AnyFunction;

        return Reflect.apply(value, target, [
          (tx: object) => callback(wrapPrismaWithCircuit(tx, withCircuit)),
          ...args.slice(1),
        ]);
      }

      return Reflect.apply(value, target, args);
    };
  }

  return new Proxy(client, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver);

      if (prop === "$transaction" && typeof value === "function") {
        if (!clientMethodCache.has(prop)) {
          clientMethodCache.set(
            prop,
            wrapTransaction(value as AnyFunction, target)
          );
        }

        return clientMethodCache.get(prop);
      }

      if (
        typeof prop === "string" &&
        typeof value === "function" &&
        !PRISMA_PROXY_SKIP.has(prop)
      ) {
        if (!clientMethodCache.has(prop)) {
          clientMethodCache.set(prop, (...args: unknown[]) =>
            withCircuit(async () =>
              Reflect.apply(value as AnyFunction, target, args)
            )
          );
        }

        return clientMethodCache.get(prop);
      }

      if (value && typeof value === "object" && typeof prop === "string") {
        if (prop.startsWith("$") || prop.startsWith("_")) {
          return value;
        }

        return wrapModelDelegate(value as object);
      }

      return value;
    },
  }) as TClient;
}

const globalForPrisma = globalThis as typeof globalThis & {
  transactionalBasePrisma?: PrismaClientWithQueryEvents;
  broadcastBasePrisma?: PrismaClientWithQueryEvents;
  reportingBasePrisma?: PrismaClientWithQueryEvents;
  exportBasePrisma?: PrismaClientWithQueryEvents;
  prisma?: PrismaClientWithQueryEvents;
  broadcastPrisma?: PrismaClientWithQueryEvents;
  reportingPrisma?: PrismaClientWithQueryEvents;
  exportPrisma?: PrismaClientWithQueryEvents;
};

function broadcastDatasourceUrl(): string | undefined {
  const raw = process.env.BROADCAST_DATABASE_URL ?? process.env.DATABASE_URL;
  if (!raw) return undefined;
  const configured = Number(process.env.BROADCAST_DB_MAX_POOL_SIZE ?? 3);
  const maxPoolSize =
    Number.isInteger(configured) && configured > 0 ? configured : 3;
  try {
    const url = new URL(raw);
    url.searchParams.set("maxPoolSize", String(maxPoolSize));
    return url.toString();
  } catch {
    throw new Error(
      "Broadcast database URL is invalid; refusing to start an uncapped broadcast pool"
    );
  }
}

/**
 * One PrismaClient (connection pool) per workload.
 * Sharing a single base client let reporting aggregations starve checkout/auth
 * and open db-transactional even though circuits are named separately.
 * Pool sizing comes from DATABASE_URL (e.g. maxPoolSize=5) — each client
 * inherits that, so 3 clients ≈ 3× that cap across the process.
 */
function getBasePrismaClient(pool: PrismaPool): PrismaClientWithQueryEvents {
  if (pool === "broadcast") {
    return (globalForPrisma.broadcastBasePrisma ??= createBasePrismaClient(
      broadcastDatasourceUrl()
    ));
  }
  if (pool === "reporting") {
    return (globalForPrisma.reportingBasePrisma ??= createBasePrismaClient());
  }
  if (pool === "export") {
    return (globalForPrisma.exportBasePrisma ??= createBasePrismaClient());
  }
  return (globalForPrisma.transactionalBasePrisma ??= createBasePrismaClient());
}

function createPrismaClient(withCircuit: CircuitRunner, pool: PrismaPool) {
  return wrapPrismaWithCircuit(getBasePrismaClient(pool), withCircuit);
}

function createLazyPrismaClient(
  create: () => PrismaClientWithQueryEvents
): PrismaClientWithQueryEvents {
  let client: PrismaClientWithQueryEvents | undefined;
  const getClient = () => (client ??= create());
  return new Proxy({} as PrismaClientWithQueryEvents, {
    get(_target, prop) {
      const resolved = getClient();
      const value = Reflect.get(resolved, prop, resolved);
      return typeof value === "function" ? value.bind(resolved) : value;
    },
  });
}

const isServer = typeof window === "undefined";

const browserPrisma = new Proxy({} as PrismaClientWithQueryEvents, {
  get() {
    throw new Error("PrismaClient can only be used on the server.");
  },
});

export const prisma: PrismaClientWithQueryEvents = isServer
  ? (globalForPrisma.prisma ??= createPrismaClient(
      withDbTransactionalCircuit,
      "transactional"
    ))
  : browserPrisma;

/**
 * Lazily-created write client for notification broadcast fan-out. Its Mongo
 * pool is independently capped so a large fan-out cannot consume booking's
 * transactional pool, including when the same worker process also books.
 */
export const broadcastPrisma: PrismaClientWithQueryEvents = isServer
  ? (globalForPrisma.broadcastPrisma ??= createLazyPrismaClient(() =>
      createPrismaClient(withDbBroadcastCircuit, "broadcast")
    ))
  : browserPrisma;

export const reportingPrisma: PrismaClientWithQueryEvents = isServer
  ? (globalForPrisma.reportingPrisma ??= createPrismaClient(
      withDbReportingCircuit,
      "reporting"
    ))
  : browserPrisma;

export const exportPrisma: PrismaClientWithQueryEvents = isServer
  ? (globalForPrisma.exportPrisma ??= createPrismaClient(
      withDbExportCircuit,
      "export"
    ))
  : browserPrisma;

export * from "@prisma/client";
